// Copyright 2020 Drivetrain Hub LLC
// GraphQL handlers for units of measurement.
// Also see math.js for more general functions for use throughout app.

import math from "../../assets/js/math";
import {isObj} from "../../assets/js/utils";

export const UNITS = {
  // <type>: <unit>
  angle: 'rad',
  length: 'mm',
  lengthSmall: 'um',  // micrometers
  perInch: '1/in',  // diametral pitch
  force: 'N',
  pressure: 'MPa',
  angularVelocity: 'rad/s',
  linearVelocity: 'mm/s',
  frequency: 'Hz',
  torque: 'Nmm',  // N*mm = kN*m
  power: 'mW',  // N*mm * rad/s
  percentage: '-',  // decimal
  time: 's',
  none: '-',
};

// WARNING: Functions are designed to convert units if they are called.
// If this proves problematic; consider adding default to switch-cases:
//  return fromUnits === toUnits ? val : undefined;

export function getUnitAngle(units) {
  return units.angle ? units.angle : UNITS.angle;
}

export function convertAngle(val, fromUnits=UNITS.angle, toUnits=UNITS.angle) {
  switch (`${fromUnits}-${toUnits}`) {
    case 'rad-deg':
      return math.rad2deg(val);
    case 'deg-rad':
      return math.deg2rad(val);
  }
}

export function convertLength(val, fromUnits=UNITS.length, toUnits=UNITS.length) {
  switch (`${fromUnits}-${toUnits}`) {
    case 'm-um':
      return val * 1e3;
    case 'um-m':
      return val / 1e3;
  }
}

export function convertAngularVelocity(val, fromUnits=UNITS.angularVelocity, toUnits=UNITS.angularVelocity) {
  switch (`${fromUnits}-${toUnits}`) {
    case 'rpm-rad/s':
      return math.rpm2radPerSec(val);
    case 'rad/s-rpm':
      return math.radPerSec2rpm(val);
  }
}

export function convertLinearVelocity(val, fromUnits=UNITS.linearVelocity, toUnits=UNITS.linearVelocity) {
  switch (`${fromUnits}-${toUnits}`) {
    case 'mm/s-m/s':
      return val / 1000;
    case 'm/s-mm/s':
      return val * 1000;
  }
}

export function convertForce(val, fromUnits=UNITS.force, toUnits=UNITS.force) {
  switch (`${fromUnits}-${toUnits}`) {
    case 'kN-N':
      return val * 1e3;
    case 'N-kN':
      return val / 1e3;
  }
}

export function convertTorque(val, fromUnits=UNITS.torque, toUnits=UNITS.torque) {
  switch (`${fromUnits}-${toUnits}`) {
    case 'Nm-Nmm':
      return val * 1e3;
    case 'Nmm-Nm':
      return val / 1e3;
  }
}

export function convertPower(val, fromUnits=UNITS.power, toUnits=UNITS.power) {
  switch (`${fromUnits}-${toUnits}`) {
    case 'mW-kW':
      return math.mW2kW(val);
    case 'kW-mW':
      return math.kW2mW(val);
  }
}

export function convertPercentage(val, fromUnits=UNITS.percentage, toUnits=UNITS.percentage) {
  switch (`${fromUnits}-${toUnits}`) {
    case '--%':
      return val * 100;
    case '%--':
      return val / 100;
  }
}

export function convertTime(val, fromUnits=UNITS.time, toUnits=UNITS.time) {
  switch (`${fromUnits}-${toUnits}`) {
    case 's-hr':
      return val / 3600;
    case 'hr-s':
      return val * 3600;
  }
}

/**
 * Convert units of object properties.
 * @param {Object} obj - Object with params to convert.  Function *mutates* this object.
 * @param {Object} objInfo - Object with lookup info.
 * @param {object} fromUnits - Units to convert from.  Defaults to unit system default.
 * @param {object} toUnits - Units to convert to.  Defaults to unit system default.
 * @param {boolean} recursive - Flag to recursively operate on the input object to convert units.
 * @return {Object} Mutated input object.
 */
export function mutateUnits(obj, objInfo, fromUnits=UNITS, toUnits=UNITS, recursive=false) {
  // for each param, convert units if applicable
  for (let [key, value] of Object.entries(obj)) {
    // recursively convert units
    if (isObj(value) && recursive) {
      // console.log('recursively convert', key);
      mutateUnits(obj[key], objInfo, fromUnits, toUnits, recursive);
    } else {
      // skip undefined keys and null values
      if (typeof objInfo[key] === 'undefined' || value === null) continue;

      // convert based on unit type
      const unitType = objInfo[key][2];
      switch (unitType) {
        case 'angle': {
          obj[key] = convertAngle(value, fromUnits.angle, toUnits.angle);
          break;
        }
        case 'angularVelocity': {
          obj[key] = convertAngularVelocity(value, fromUnits.angularVelocity, toUnits.angularVelocity);
          break;
        }
        case 'linearVelocity': {
          obj[key] = convertLinearVelocity(value, fromUnits.linearVelocity, toUnits.linearVelocity);
          break;
        }
        case 'force': {
          obj[key] = convertForce(value, fromUnits.force, toUnits.force);
          break;
        }
        case 'torque': {
          obj[key] = convertTorque(value, fromUnits.torque, toUnits.torque);
          break;
        }
        case 'power': {
          obj[key] = convertPower(value, fromUnits.power, toUnits.power);
          break;
        }
        case 'percentage': {
          obj[key] = convertPercentage(value, fromUnits.percentage, toUnits.percentage);
          break;
        }
        case 'time': {
          obj[key] = convertTime(value, fromUnits.time, toUnits.time);
          break;
        }
      }
    }
  }

  return obj;
}
